import React, { useState, useMemo } from 'react';
import { Card, CardBody, Row, Col, Label, Input, ListGroup, ListGroupItem, Button, Table } from 'reactstrap';
import { InvoiceDetail } from '../types';
import { formatDecimal } from '../../../utils/numberUtils';

interface Props {
    details: InvoiceDetail[];
    taxTypes: any[];
    onAddProduct: (product: any) => void;
    onAddEmpty: () => void;
    onUpdateDetail: (id: string, field: keyof InvoiceDetail, value: any) => void;
    onRemoveDetail: (id: string) => void;
    showValidationErrors: boolean;
}

const InvoiceDetails = ({ details, taxTypes, onAddEmpty, onUpdateDetail, onRemoveDetail, showValidationErrors }: Props) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [showProductList, setShowProductList] = useState(false);
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

    const validateQuantity = (value: number) => {
        if (value < 0) return "La cantidad no puede ser negativa";
        return "";
    };

    const validatePrice = (value: number) => {
        if (value < 0) return "El precio no puede ser negativo";
        return "";
    };

    const validateDiscount = (discount: number, quantity: number, price: number) => {
        if (discount < 0) return "El descuento no puede ser negativo";
        if (discount > (quantity * price)) return "El descuento excede el total";
        return "";
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>, id: string, field: keyof InvoiceDetail) => {
        setTouched(prev => ({ ...prev, [`${id}-${field}`]: true }));

        const isNumeric = field === 'quantity' || field === 'unitPrice' || field === 'discount';
        if (isNumeric) {
            let value = e.target.value;
            value = value.replace(',', '.');
            const numberValue = parseFloat(value);
            onUpdateDetail(id, field, isNaN(numberValue) || value === '' ? 0 : numberValue);
        }
    };

    // Recolectar todos los errores
    const validationErrors = useMemo(() => {
        const errors: string[] = [];
        details.forEach((item, index) => {
            const lineNum = index + 1;
            const fieldTouched = (field: keyof InvoiceDetail) => touched[`${item.id}-${field}`] || showValidationErrors;

            if (fieldTouched("mainCode") && !item.mainCode) {
                errors.push(`Línea ${lineNum}: Falta el código del producto.`);
            }
            if (fieldTouched("description") && !item.description) {
                errors.push(`Línea ${lineNum}: Falta la descripción del producto.`);
            }
            
            const qtyErr = validateQuantity(item.quantity);
            if (fieldTouched("quantity") && qtyErr) {
                errors.push(`Línea ${lineNum}: ${qtyErr}`);
            }

            const priceErr = validatePrice(item.unitPrice);
            if (fieldTouched("unitPrice") && priceErr) {
                errors.push(`Línea ${lineNum}: ${priceErr}`);
            }

            const discountErr = validateDiscount(item.discount, item.quantity, item.unitPrice);
            if (fieldTouched("discount") && discountErr) {
                errors.push(`Línea ${lineNum}: ${discountErr}`);
            }
        });
        return errors;
    }, [details, touched, showValidationErrors]);

    return (
        <Card className="shadow border-0 h-100 overflow-hidden rounded-3 mb-4">
            <div className="bg-soft-primary p-3 border-bottom border-light">
                <h5 className="card-title mb-0 text-primary fw-semibold d-flex align-items-center">
                    <i className="ri-shopping-cart-fill me-2 fs-18"></i>
                    Detalles de la Factura
                </h5>
            </div>
            <CardBody className="p-4 bg-white">

            <Card className="border shadow-none mb-3">
                <CardBody className="p-3 bg-light rounded">
                    <Row className="align-items-end g-3">
                        <Col lg={8} md={8}>
                            <Label className="form-label fw-bold">Buscar Producto</Label>
                            <div className="position-relative">
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0"><i className="ri-search-line"></i></span>
                                    <Input
                                        type="text"
                                        placeholder="Escriba código o nombre del producto..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setShowProductList(true);
                                        }}
                                        onFocus={() => setShowProductList(true)}
                                        className="border-start-0"
                                    />
                                </div>
                                {showProductList && searchTerm.length > 0 && (
                                    <div className="position-absolute w-100 mt-1 shadow-lg rounded-3 overflow-hidden" style={{ zIndex: 1000, backgroundColor: 'white' }}>
                                        <ListGroup flush style={{ maxHeight: '250px', overflowY: 'auto' }}>

                                                <ListGroupItem

                                                    action

                                                    className="d-flex justify-content-between align-items-center p-3 border-bottom-0"
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div>
                                                        <h6 className="mb-1 text-dark">Detalles del producto</h6>
                                                        <small className="text-muted">Código: </small>
                                                    </div>
                                                    <span className="badge bg-soft-success text-success rounded-pill fs-6"></span>
                                                </ListGroupItem>
                                            ))
                                        </ListGroup>
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col lg={4} md={4}>
                            <div className="d-flex gap-2">
                                <Button color="primary" className="flex-grow-1" onClick={() => { }}><i className="ri-search-line me-1"></i> Buscar</Button>
                                <Button color="soft-secondary" onClick={onAddEmpty} title="Agregar línea vacía"><i className="ri-add-line me-1"></i> Manual</Button>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            </CardBody>

            <CardBody className="p-0 bg-white border-top">
                <div className="table-responsive">
                    <Table className="table-nowrap align-middle mb-0 table-hover" style={{ minWidth: '1000px' }}>
                        <thead className="bg-light text-muted text-uppercase small">
                        <tr>
                            <th style={{ width: '10%' }}>Código<span className="text-danger">*</span></th>
                            <th style={{ width: '10%' }}>Cód. Auxiliar</th>
                            <th style={{ width: '25%' }}>Descripción<span className="text-danger">*</span></th>
                            <th style={{ width: '10%' }}>Cantidad</th>
                            <th style={{ width: '10%' }}>P. Unitario</th>
                            <th style={{ width: '10%' }}>Descuento</th>
                            <th style={{ width: '12%' }}>
                                Impuesto {taxTypes.length === 0 && (
                                    <span className="text-muted small">(cargando...)</span>
                                )}
                            </th>
                            <th style={{ width: '10%' }}>
                                Tarifa {taxTypes.length === 0 && (
                                    <span className="text-muted small">(cargando...)</span>
                                )}
                            </th>
                            <th style={{ width: '10%' }} className="text-end">Total</th>
                            <th style={{ width: '3%' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {details.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="text-center py-5">
                                    <div className="avatar-sm mx-auto mb-3">
                                        <div className="avatar-title bg-soft-light rounded-circle text-primary fs-24">
                                            <i className="ri-shopping-basket-line"></i>
                                        </div>
                                    </div>
                                    <h5 className="text-muted mb-1">No hay items en la factura</h5>
                                    <p className="text-muted mb-0 small">Agregue productos para comenzar</p>
                                </td>
                            </tr>
                        ) : (
                            details.map((item, index) => {
                                const quantityError = validateQuantity(item.quantity);
                                const priceError = validatePrice(item.unitPrice);
                                const discountError = validateDiscount(item.discount, item.quantity, item.unitPrice);
                                
                                const fieldTouched = (field: keyof InvoiceDetail) => touched[`${item.id}-${field}`] || showValidationErrors;
                                const isCodeInvalid = fieldTouched("mainCode") && !item.mainCode;
                                const isDescInvalid = fieldTouched("description") && !item.description;
                                const isQtyInvalid = fieldTouched("quantity") && !!quantityError;
                                const isPriceInvalid = fieldTouched("unitPrice") && !!priceError;
                                const isDiscountInvalid = fieldTouched("discount") && !!discountError;

                                return (
                                <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-soft-light bg-opacity-25'}>
                                    <td style={{ verticalAlign: 'top' }}>
                                        <Input 
                                            value={item.mainCode} 
                                            onChange={(e) => onUpdateDetail(item.id, "mainCode", e.target.value)} 
                                            onBlur={(e) => handleBlur(e, item.id, "mainCode")} 
                                            bsSize="sm" 
                                            className="bg-transparent p-1"
                                            placeholder="Código" 
                                            invalid={isCodeInvalid} 
                                        />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                        <Input
                                            value={item.auxiliaryCode}
                                            onChange={(e) => onUpdateDetail(item.id, "auxiliaryCode", e.target.value)}
                                            onBlur={(e) => handleBlur(e, item.id, "auxiliaryCode")}
                                            bsSize="sm"
                                            className="bg-transparent p-1"
                                            placeholder="Código auxiliar"
                                            invalid={isDescInvalid}
                                        />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                        <Input 
                                            value={item.description} 
                                            onChange={(e) => onUpdateDetail(item.id, "description", e.target.value)} 
                                            onBlur={(e) => handleBlur(e, item.id, "description")} 
                                            bsSize="sm" 
                                            className="bg-transparent p-1"
                                            placeholder="Descripción" 
                                            invalid={isDescInvalid} 
                                        />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                        <Input
                                            type="number"
                                            min={0}
                                            step="any"
                                            value={item.quantity}
                                            onChange={(e) => {
                                                let value = e.target.value;
                                                value = value.replace(',', '.');
                                                const numberValue = parseFloat(value);
                                                onUpdateDetail(item.id, "quantity", isNaN(numberValue) || value === '' ? 0 : numberValue);
                                            }}
                                            onKeyDown={(e) => {
                                                if (["-", "+", "e", "E"].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            bsSize="sm"
                                            className="text-center"
                                            invalid={isQtyInvalid}
                                        />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                        <Input
                                            type="number"
                                            min={0}
                                            step="any"
                                            value={item.unitPrice}
                                            onChange={(e) => {
                                                let value = e.target.value;
                                                value = value.replace(',', '.');
                                                const numberValue = parseFloat(value);
                                                onUpdateDetail(item.id, "unitPrice", isNaN(numberValue) || value === '' ? 0 : numberValue);
                                            }}
                                            onKeyDown={(e) => {
                                                if (["-", "+", "e", "E"].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            bsSize="sm"
                                            className="text-end"
                                            invalid={isPriceInvalid}
                                        />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                        <Input
                                            type="number"
                                            min={0}
                                            step="any"
                                            value={item.discount}
                                            onChange={(e) => {
                                                let value = e.target.value;
                                                value = value.replace(',', '.');
                                                const numberValue = parseFloat(value);
                                                onUpdateDetail(item.id, "discount", isNaN(numberValue) || value === '' ? 0 : numberValue);
                                            }}
                                            onKeyDown={(e) => {
                                                if (["-", "+", "e", "E"].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            bsSize="sm"
                                            className="text-end"
                                            invalid={isDiscountInvalid}
                                        />
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                        {taxTypes.length === 0 ? (
                                            <Input type="select" value={""} disabled bsSize="sm" className="form-select-sm">
                                                <option value="">Cargando impuestos...</option>
                                            </Input>
                                        ) : (
                                            <Input
                                                type="select"
                                                value={item.selectedTaxId}
                                                onChange={(e) => onUpdateDetail(item.id, "selectedTaxId", e.target.value)}
                                                bsSize="sm"
                                                className="form-select-sm"
                                            >
                                                <option value="">Seleccione impuesto</option>
                                                {taxTypes.map((tax: any) => (
                                                    <option key={tax.id} value={tax.id}>{tax.name}</option>
                                                ))}
                                            </Input>
                                        )}
                                    </td>
                                    <td style={{ verticalAlign: 'top' }}>
                                        {taxTypes.length === 0 || !taxTypes.find((t: any) => t.id === item.selectedTaxId) ? (
                                            <Input type="select" value={""} disabled bsSize="sm" className="form-select-sm">
                                                <option value="">Seleccione impuesto primero</option>
                                            </Input>
                                        ) : (
                                            <Input
                                                type="select"
                                                value={item.selectedRateId}
                                                onChange={(e) => onUpdateDetail(item.id, "selectedRateId", e.target.value)}
                                                bsSize="sm"
                                                className="form-select-sm"
                                            >
                                                {taxTypes
                                                    .find((t: any) => t.id === item.selectedTaxId)
                                                    ?.children
                                                    .filter((rate: any) => rate.jsonData?.isVisible !== false)
                                                    .map((rate: any) => (
                                                        <option key={rate.id} value={rate.id}>{rate.name}</option>
                                                    ))}
                                            </Input>
                                        )}
                                    </td>
                                    <td className="text-end fw-bold" style={{ verticalAlign: 'top' }}>{formatDecimal((item.quantity * item.unitPrice) - item.discount)}</td>
                                    <td className="text-end" style={{ verticalAlign: 'top' }}>
                                        <Button 
                                            color="soft-danger" 
                                            size="sm" 
                                            className="btn-icon shadow-none" 
                                            onClick={() => onRemoveDetail(item.id)}
                                            title="Eliminar línea"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </Button>
                                    </td>
                                </tr>
                            )})
                        )}
                    </tbody>
                </Table>
            </div>
            {validationErrors.length > 0 && (
                <div className="alert alert-danger mx-4 my-3">
                    <div className="d-flex align-items-center mb-2">
                        <i className="ri-error-warning-line me-2 fs-5"></i>
                        <h6 className="mb-0 alert-heading">Se encontraron los siguientes errores:</h6>
                    </div>
                    <ul className="mb-0 ps-3">
                        {validationErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}
            </CardBody>
        </Card>
    );
};

export default InvoiceDetails;
